<template>
  <el-card shadow="never" :class="{ 'tech-card': tech }">
    <template v-if="!tech" #header>
      <CardTitle title="快捷入口" />
    </template>
    <div class="shortcut-grid" :class="{ 'tech-grid': tech }">
      <button
        v-for="menu in menuList"
        :key="menu.name"
        type="button"
        class="shortcut-item"
        :class="{ 'is-disabled': !navigate }"
        :disabled="!navigate"
        @click="handleMenuClick(menu)"
      >
        <div class="shortcut-icon" :class="tech ? 'tech-icon' : menu.bgColor">
          <Icon :icon="menu.icon" :size="22" />
        </div>
        <span class="shortcut-label" :class="{ 'tech-label': tech }">{{ menu.name }}</span>
      </button>
    </div>
  </el-card>
</template>
<script lang="ts" setup>
import { CardTitle } from '@/components/Card'
import { useMallStandaloneNav } from '@/hooks/web/useMallStandaloneNav'

/** 快捷入口卡片（概况日常跳转；驾驶舱不再承载） */
defineOptions({ name: 'ShortcutCard' })

const props = withDefaults(
  defineProps<{
    tech?: boolean
    navigate?: boolean
  }>(),
  { tech: false, navigate: true }
)

const router = useRouter()
const { pushMall } = useMallStandaloneNav()

type ShortcutItem = {
  name: string
  icon: string
  bgColor: string
  /** 优先按路由 name 跳转（商城菜单内） */
  routerName?: string
  /** 商城独立窗内相对路径，如 /member/user（优先于跨模块 MemberUser） */
  mallPath?: string
}

const menuList: ShortcutItem[] = [
  {
    name: '用户管理',
    icon: 'ep:user-filled',
    bgColor: 'bg-red-400',
    // 商城侧栏「用户管理 → 用户列表」，勿跳会员中心裸路由 MemberUser
    routerName: 'MallMemberUser',
    mallPath: '/member/user'
  },
  {
    name: '商品管理',
    icon: 'fluent-mdl2:product',
    bgColor: 'bg-orange-400',
    routerName: 'ProductSpu'
  },
  { name: '订单管理', icon: 'ep:list', bgColor: 'bg-yellow-500', routerName: 'TradeOrder' },
  {
    name: '售后管理',
    icon: 'ri:refund-2-line',
    bgColor: 'bg-green-600',
    routerName: 'TradeAfterSale'
  },
  {
    name: '分销管理',
    icon: 'fa-solid:project-diagram',
    bgColor: 'bg-cyan-500',
    routerName: 'TradeBrokerageUser'
  },
  {
    name: '优惠券',
    icon: 'ep:ticket',
    bgColor: 'bg-blue-500',
    routerName: 'PromotionCoupon'
  },
  {
    name: '拼团活动',
    icon: 'fa:group',
    bgColor: 'bg-purple-500',
    routerName: 'PromotionCombinationActivity'
  },
  {
    name: '佣金提现',
    icon: 'vaadin:money-withdraw',
    bgColor: 'bg-rose-500',
    // 与后台菜单 component_name 一致（system_menu 2355：TradeBrokerageWithdraw）
    routerName: 'TradeBrokerageWithdraw'
  }
]

const handleMenuClick = (menu: ShortcutItem) => {
  if (!props.navigate) return
  // 用户管理：优先走商城前缀路径，保证独立窗侧栏不丢
  if (menu.mallPath) {
    void pushMall(menu.mallPath)
    return
  }
  if (menu.routerName) {
    const aiName = `${menu.routerName}AiAppCenter`
    if (router.hasRoute(aiName)) {
      void router.push({ name: aiName })
      return
    }
    if (router.hasRoute(menu.routerName)) {
      void router.push({ name: menu.routerName })
      return
    }
  }
}
</script>

<style scoped lang="scss">
.shortcut-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 8px;
  padding: 4px 4px 8px;
}

.shortcut-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 88px;
  padding: 12px 8px;
  font: inherit;
  color: inherit;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 8px;
  transition: background-color 0.15s ease;

  &:hover:not(.is-disabled) {
    background: var(--el-fill-color-light);
  }

  &.is-disabled {
    cursor: default;
  }
}

.shortcut-icon {
  display: flex;
  width: 48px;
  height: 48px;
  color: #fff;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
}

.shortcut-label {
  font-size: 13px;
  line-height: 18px;
  color: var(--el-text-color-regular);
  text-align: center;
}

.tech-card {
  color: #e6f7ff;
  background: transparent;
  border: none;
}

.tech-grid {
  padding: 8px 4px 12px;
}

.tech-icon {
  background: linear-gradient(135deg, rgb(28 156 255 / 85%), rgb(0 242 254 / 55%));
  box-shadow: 0 0 12px rgb(0 242 254 / 25%);
}

.tech-label {
  color: #9fc4ee;
  font-size: 12px;
}

.tech-card .shortcut-item:hover:not(.is-disabled) {
  background: rgb(0 242 254 / 8%);
}

@media (width <= 900px) {
  .shortcut-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
